import React from "react";

const ControllablesVideo = ({ subsection, saveProgress }) => {
  const about = (
    <div className="space-y-5 pt-2 pb-6 text-body leading-7 text-[#484848]">
      <p>
      This lesson shows how promotion speed changes when you focus on controllables. Through Mira and Avi, it explores choices that compound: picking work that pays back in impact, visibility, and repeatability, adapting to how stakeholders operate, asking early for readiness signals, building a reputation that travels, using feedback to create buy in, and protecting energy so consistency does not collapse.
      </p>
    </div>
  );

  const transcriptHtml = `
    <p>Pause and look back at the last 1 year of your career. See the pattern? Progress showed up when you took charge of your effort, your clarity, your behaviour, not when the stars aligned, your boss was in a "good mood," or the universe felt generous. These are your <strong>controllables.</strong></p>

    <p>Let's break it down.</p>

    <h3 style="margin-top: 24px; margin-bottom: 12px; font-weight: 700; font-size: 1.125rem;">Meet Mira and Avi</h3>
    <p>Both are talented, dedicated, and consistently meet expectations.</p>
    <p>But one of them, Mira, gets promoted almost a full cycle earlier.</p>
    <p>Not because she's luckier.</p>
    <p>But because she leveraged what was in her control to get promoted.</p>

    <p>Mira might seem naturally promotable. But it's not innate, it's learned. She focused on her controllables more than the effort she put in.</p>

    <h3 style="margin-top: 24px; margin-bottom: 12px; font-weight: 700; font-size: 1.125rem;">Choosing the Right Work:</h3>
    <p>Both Mira and Avi get the same email: "New project available, optional support role."</p>
    <p>Avi shrugs and sends an acceptance email</p>
    <p>Mira thinks: "I'd like to explore this, it aligns with my values: Then she asks her manager, <em>"How critical is this project to you?"</em></p>

    <p>Not every project is the same.</p>
    <p>Some projects build visibility, some skill, some just build stress, some are critical for the company while some are just risky with a bad client which nobody wants to touch.</p>

    <p>Promotable professionals know the difference. They create opportunities for themselves because they understand what is the amount of risk in the project and what is the expected return on that project.</p>
    <p>Their aim is to look for projects that multiply three things: <strong>Impact × Visibility × Repeatability.</strong></p>

    <h3 style="margin-top: 24px; margin-bottom: 12px; font-weight: 700; font-size: 1.125rem;">Understanding Your Stakeholders Wants (Not just needs):</h3>
    <p>Avi sends regular updates: "Here's what I'm working on, these are the deadlines, these are the blockers."</p>
    <p>Mira does something different. She pays attention to how her manager works. She notices: does he want hands-on work, or just updates? Does he value speed, or thoroughness? Then she gives exactly that.</p>
    <p>If the boss wants updates, she keeps them coming.</p>
    <p>If the boss wants to work, she gives him more work.</p>
    <p>Promotable people know it's not about how you work, it's about how your boss works. They match their style, pace, and priorities to what matters most to the person who decides.</p>

    <h3 style="margin-top: 24px; margin-bottom: 12px; font-weight: 700; font-size: 1.125rem;">Ask Early:</h3>
    <p>Avi asked for a promotion, but only when one of his colleagues got promoted.</p>
    <p>But Mira doesn't wait. The day she got her current role, she booked a quick sync with her manager:</p>
    <p><em>"Can you help me understand what readiness for the next level looks like?"</em></p>

    <p>3 months later Avi is still at where he was and Mira is already acting on feedback, aligning work, and making her next move visible.</p>

    <p>Most people never ask for a promotion because of fear of rejection.</p>
    <p>But the most promotable people ask early. They get clarity on what the next level requires, and start acting on it now.</p>

    <h3 style="margin-top: 24px; margin-bottom: 12px; font-weight: 700; font-size: 1.125rem;">Build A Personal Brand:</h3>
    <p>While Avi is busy just completing his tasks, Mira is thinking bigger. She builds a personal brand that's aligned with the next level. If the role above demands tough negotiation, she makes herself known as the go-to negotiator.</p>

    <p>In leadership meetings, her name naturally comes up:</p>
    <p><em>"She handled that project really well."</em> Avi? Almost invisible.</p>
    <p>Promotable professionals build a brand that works for them even when they're not in the room.</p>
    <p>Their reputation compounds like interest.</p>

    <h3 style="margin-top: 24px; margin-bottom: 12px; font-weight: 700; font-size: 1.125rem;">Seek Strategic Feedback</h3>
    <p>Avi and Mira both ask for feedback, but Avi does it for validation and appreciation, basically, "Did you like my work?"</p>
    <p>Mira asks with a different goal: to get her manager invested in her growth. After finishing a task, she might say, "You handled that client really well, how can I develop that skill?" Her manager smiles and starts taking a personal interest in her progress.</p>
    <p>Think about it, wouldn't you help improve something you had a hand in building?</p>
    <p>Each time you ask for feedback you turn your stakeholder into a co-owner of your success.</p>

    <h3 style="margin-top: 24px; margin-bottom: 12px; font-weight: 700; font-size: 1.125rem;">Protect Your Energy:</h3>
    <p>Avi is buried under tasks and back-to-back meetings.</p>
    <p>Mira blocks time on her calendar for deep work, declines low-value meetings, and delegates administrative tasks.</p>
    <p>Promotable people don't burn out. They prioritize energy like a resource.</p>
    <p>They say no to noise and yes to leverage.</p>
    <p>Because performance fades when energy drains.</p>

    <p>Every cycle, a few people get promoted ahead of schedule.</p>
    <p>Not because they're special, but because they've mastered what's within reach.</p>

    <p>These skills aren't innate, they can be cultivated.</p>

    <p><strong>Which of these controllables will you double down on?</strong></p>
  `;

  const transcript = (
    <div
      className="pt-2 pb-6 text-body leading-7 text-[#484848] max-w-2xl"
      dangerouslySetInnerHTML={{ __html: transcriptHtml }}
    />
  );

  const notes = (
    <div className="pt-2 pb-6">
      <textarea
        rows={4}
        placeholder="Write your notes here..."
        className="w-full border border-gray-200 rounded-xl p-3 text-body focus:ring-2 focus:ring-brand/40 focus:outline-none"
        onBlur={(e) => saveProgress("in_progress", { notes: e.target.value })}
      />
    </div>
  );

  return { about, transcript, notes };
};

export default ControllablesVideo;

